import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { ReactComponent as AngleRight } from '~/assets/images/angle-right.svg';
import { getValidBreadcrumbLevels } from '~/utils/hosting/helpers';

export type RouteLevel = {
  label: string;
  navigateTo?: string;
};
interface BreadcrumbsProps {
  levels: RouteLevel[];
}

const textClampSx = {
  maxWidth: { xs: 120, sm: 250, md: 400 },
  py: 0.5,
  pr: 0,
  pl: 1,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const Breadcrumbs = ({ levels }: BreadcrumbsProps) => {
  const validLevels = useMemo(() => getValidBreadcrumbLevels(levels), [levels]);

  if (validLevels.length < 2) {
    return null;
  }

  const trailLevels = validLevels.slice(0, -1);
  const currentLevel = validLevels.at(-1);

  return (
    <MuiBreadcrumbs
      separator={<AngleRight />}
      aria-label="Breadcrumbs"
      data-testid="breadcrumbs"
    >
      {trailLevels.map(({ navigateTo, label }) =>
        navigateTo ? (
          <Link
            key={label}
            component={RouterLink}
            to={navigateTo}
            sx={{ textDecoration: 'none' }}
            color="text.secondary"
          >
            <Typography variant="body2" fontWeight={500}>
              {label}
            </Typography>
          </Link>
        ) : (
          <Typography key={label} variant="body2" sx={textClampSx}>
            {label}
          </Typography>
        ),
      )}
      {currentLevel && (
        <Typography variant="body2" sx={textClampSx}>
          {currentLevel.label}
        </Typography>
      )}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
